import React from 'react';
import { ShieldCheck, Scale, FileText } from 'lucide-react';

function ConditionsOfUse({ currentLang }) {
  const legalContent = {
    en: {
      title: "Conditions of Use & Terms of Service",
      subtitle: "Legal Framework & Binding Agreement",
      alert: "IMPORTANT LEGAL NOTICE: By accessing this website, purchasing learning materials, downloading worksheets, or scheduling any booking services, you automatically, instantly, and unconditionally agree to be fully bound by all terms detailed below. If you do not agree, you must immediately terminate use of this platform.",
      sec1Title: "1. Automatic and Binding Agreement",
      sec1Text: "The execution of any action on this platform—including browsing digital pages, completing educational transactions, or reserving calendar booking sessions—serves as your electronic signature and objective manifestation of assent. This agreement is legally binding between you and Pterόessa.",
      sec2Title: "2. Intellectual Property & Digital Rights",
      sec2Text: "All historical materials, philological lesson guides, worksheets, and media files available here or via connected stores (such as Eduki) remain the exclusive intellectual property of Pterόessa. Your purchase grants you a single-user, non-transferable, revocable license for individual educational purposes only. Redistribution, re-selling, or unauthorized copying is strictly prohibited under international copyright laws.",
      sec3Title: "3. Disclaimers & Limitation of Liability",
      sec3Text: "Educational services and materials are provided on an 'as-is' and 'as-available' basis. While high pedagogical standards are maintained, Pterόessa offers no warranties regarding specific individual academic performance, grades, or exam outcomes. Liability is restricted to the maximum extent permitted by applicable laws."
    },
    gr: {
      title: "Όροι Χρήσης & Προϋποθέσεις",
      subtitle: "Νομικό Πλαίσιο & Δεσμευτική Συμφωνία",
      alert: "ΣΗΜΑΝΤΙΚΗ ΝΟΜΙΚΗ ΣΗΜΕΙΩΣΗ: Με την πλοήγηση σε αυτόν τον ιστότοπο, την αγορά εκπαιδευτικού υλικού, τη λήψη αρχείων ή τον προγραμματισμό μαθημάτων, συμφωνείτε αυτόματα, άμεσα και ανεπιφύλακτα με όλους τους παρακάτω όρους. Εάν δεν συμφωνείτε, οφείλετε να διακόψετε αμέσως τη χρήση της πλατφόρμας.",
      sec1Title: "1. Αυτόματη και Δεσμευτική Συμφωνία",
      sec1Text: "Η εκτέλεση οποιασδήποτε ενέργειας σε αυτήν την πλατφόρμα αποτελεί την ηλεκτρονική σας υπογραφή και αντικειμενική δήλωση συναίνεσης. Η παρούσα συμφωνία είναι νομικά δεσμευτική ανάμεσα σε εσάς και την Πτερόεσσα.",
      sec2Title: "2. Πνευματική Ιδιοκτησία & Ψηφιακά Δικαιώματα",
      sec2Text: "Όλο το ιστορικό και φιλολογικό υλικό, τα φύλλα εργασίας και τα αρχεία που διατίθενται εδώ ή μέσω συνεργαζόμενων καταστημάτων (όπως το Eduki) αποτελούν αποκλειστική πνευματική ιδιοκτησία της Πτερόεσσας. Απαγορεύεται αυστηρά η αναδιανομή, μεταπώληση ή αντιγραφή χωρίς έγγραφη άδεια.",
      sec3Title: "3. Περιορισμός Ευθύνης",
      sec3Text: "Το εκπαιδευτικό υλικό παρέχεται 'ως έχει'. Παρόλο που τηρούνται υψηλά παιδαγωγικά πρότυπα, η Πτερόεσσα δεν παρέχει εγγυήσεις για συγκεκριμένα ακαδημαϊκά αποτελέσματα ή βαθμούς εξετάσεων."
    },
    it: {
      title: "Condizioni d'Uso & Termini di Servizio",
      subtitle: "Quadro Legale & Accordo Vincolante",
      alert: "AVVISO LEGALE IMPORTANTE: Accedendo a questo sito web, acquistando materiali didattici, scaricando schede operative o prenotando lezioni, l'utente accetta automaticamente, istantaneamente e incondizionatamente di essere vincolato da tutti i termini descritti di seguito. Se non si è d'accordo, interrompere immediatamente l'uso della piattaforma.",
      sec1Title: "1. Accordo Automatico e Vincolante",
      sec1Text: "L'esecuzione di qualsiasi azione su questa piattaforma costituisce la firma elettronica e la manifestazione oggettiva del consenso dell'utente. Questo accordo è legalmente vincolante tra l'utente e Pterόessa.",
      sec2Title: "2. Proprietà Intellettuale & Diritti Digitali",
      sec2Text: "Tutti τα materiali storici, le guide filologiche e le schede didattiche disponibili qui o tramite negozi collegati (come Eduki) rimangono proprietà intellettuale esclusiva di Pterόessa. È severamente vietata la ridistribuzione o la rivendita.",
      sec3Title: "3. Limitazione di Responsabilità",
      sec3Text: "I materiali sono forniti 'così come sono'. Pterόessa non offre garanzie in merito a specifici risultati accademici individuali o esiti d'esame."
    }
  };

  const t = legalContent[currentLang];

  return (
    <div className="row justify-content-center">
      <div className="col-lg-9 col-md-12">
        <div className="text-center mb-4">
          <ShieldCheck size={48} className="text-success mb-2" />
          <h2>{t.title}</h2>
          <span className="text-muted small text-uppercase tracking-wider font-weight-bold">{t.subtitle}</span>
        </div>

        {/* Severe Safe Gate/Enforcement Banner */}
        <div className="p-4 mb-5 border-start border-4 border-danger rounded-3" style={{ backgroundColor: '#fff5f5', color: '#a94442' }}>
          <div className="d-flex gap-2 align-items-start">
            <Scale size={24} className="flex-shrink-0 mt-1" />
            <p className="m-0 font-weight-bold" style={{ color: '#a94442', fontWeight: 600, fontSize: '15px', textAlign: 'left' }}>
              {t.alert}
            </p>
          </div>
        </div>

        {/* Legal Sub-Sections */}
        <div className="d-flex flex-column gap-4 bg-white p-4 p-md-5 rounded-4 shadow-sm border border-light">
          <div className="d-flex gap-3 align-items-start">
            <FileText size={20} className="text-muted mt-1" />
            <div>
              <h4 className="h5 mb-2">{t.sec1Title}</h4>
              <p style={{ fontSize: '15px' }}>{t.sec1Text}</p>
            </div>
          </div>

          <div className="d-flex gap-3 align-items-start">
            <FileText size={20} className="text-muted mt-1" />
            <div>
              <h4 className="h5 mb-2">{t.sec2Title}</h4>
              <p style={{ fontSize: '15px' }}>{t.sec2Text}</p>
            </div>
          </div>

          <div className="d-flex gap-3 align-items-start">
            <FileText size={20} className="text-muted mt-1" />
            <div>
              <h4 className="h5 mb-2">{t.sec3Title}</h4>
              <p style={{ fontSize: '15px' }}>{t.sec3Text}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConditionsOfUse;